import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, FileText, KeyRound, Save } from "lucide-react";
import Portrait from "@/components/Portrait";
import { fileUrl } from "@/api";
import { papi, errText } from "../portalApi";
import { usePortal } from "../PortalApp";
import { Card, Field, PageHeader, Spinner, StatusBadge, fmtDate, inputCls } from "../ui";

const EDITABLE = [
  ["phone", "Phone", "tel"],
  ["personalEmail", "Personal Email", "email"],
  ["alternatePhone", "Alternate Phone", "tel"],
  ["dateOfBirth", "Date of Birth", "date"],
  ["emergencyContact", "Emergency Contact", "text"],
  ["education", "Education", "text"],
  ["address", "Address", "text"],
  ["city", "City", "text"],
  ["state", "State", "text"],
  ["country", "Country", "text"],
  ["skills", "Skills (comma separated)", "text"],
];

export default function Profile() {
  const { refreshEmp } = usePortal();
  const [profile, setProfile] = useState(null);
  const [docs, setDocs] = useState([]);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [pwForm, setPwForm] = useState({ current: "", next: "", confirm: "" });
  const [pwSaving, setPwSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [p, d] = await Promise.all([papi.get("/profile"), papi.get("/documents")]);
      setProfile(p.data);
      setDocs(d.data);
      const f = {};
      EDITABLE.forEach(([k]) => { f[k] = p.data[k] || ""; });
      f.gender = p.data.gender || "";
      setForm(f);
    } catch (err) {
      toast.error(errText(err, "Could not load your profile."));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!profile) return <Spinner />;

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await papi.put("/profile", form);
      toast.success("Profile updated.");
      refreshEmp();
      load();
    } catch (err) {
      toast.error(errText(err, "Could not save your profile."));
    } finally {
      setSaving(false);
    }
  };

  const changePw = async (e) => {
    e.preventDefault();
    if (pwForm.next !== pwForm.confirm) {
      toast.error("New passwords do not match.");
      return;
    }
    setPwSaving(true);
    try {
      await papi.post("/change-password", { currentPassword: pwForm.current, newPassword: pwForm.next });
      toast.success("Password updated. Use it on your next sign in.");
      setPwForm({ current: "", next: "", confirm: "" });
    } catch (err) {
      toast.error(errText(err, "Could not update the password."));
    } finally {
      setPwSaving(false);
    }
  };

  const org = [
    ["Employee ID", profile.employeeId],
    ["Official Email", profile.officialEmail],
    ["Designation", profile.designation],
    ["Department", profile.department],
    ["Brand", profile.brand],
    ["Employment Type", profile.employmentType],
    ["Joining Date", fmtDate(profile.joiningDate)],
    ["Reporting Manager", profile.reportingManager || "—"],
    ["Work Mode", profile.workMode],
    ["Work Location", profile.workLocation],
  ];

  return (
    <div data-testid="profile-page">
      <PageHeader title="My Profile" sub="Your employee master record. Organisation details are managed by HR." />

      <Card className="mb-6" testid="profile-identity-card">
        <div className="flex flex-wrap items-center gap-6">
          <Portrait name={profile.fullName} src={profile.photograph || ""} size="lg" testid="profile-photo" />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-3">
              <h2 className="font-serif text-2xl text-navy-900" data-testid="profile-name">{profile.fullName}</h2>
              <StatusBadge status={profile.status} />
            </div>
            <p className="mt-1 text-sm font-semibold text-gold-dark">{profile.designation}</p>
            <p className="mt-1 text-xs text-slate-500">{profile.department} · {profile.brand}</p>
          </div>
        </div>
        <div className="mt-6 grid grid-cols-2 gap-4 border-t border-navy-900/10 pt-6 sm:grid-cols-3 lg:grid-cols-5" data-testid="profile-org-details">
          {org.map(([k, v]) => (
            <div key={k}>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{k}</p>
              <p className="mt-1 break-words text-sm font-semibold text-navy-900">{v || "—"}</p>
            </div>
          ))}
        </div>
      </Card>

      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2" testid="profile-edit-card">
          <p className="mb-5 font-serif text-lg text-navy-900">Personal Details</p>
          <form onSubmit={save} className="grid gap-4 sm:grid-cols-2" data-testid="profile-form">
            {EDITABLE.map(([key, label, type]) => (
              <Field key={key} label={label}>
                <input
                  type={type}
                  className={inputCls}
                  value={form[key] || ""}
                  onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                  data-testid={`profile-field-${key}`}
                />
              </Field>
            ))}
            <Field label="Gender">
              <select className={inputCls} value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })} data-testid="profile-field-gender">
                <option value="">Prefer not to say</option>
                <option>Female</option>
                <option>Male</option>
                <option>Other</option>
              </select>
            </Field>
            <div className="sm:col-span-2">
              <button type="submit" disabled={saving} className="btn-gold !px-6 !py-2.5 !text-xs disabled:opacity-60" data-testid="profile-save">
                <Save className="h-4 w-4" aria-hidden /> {saving ? "Saving…" : "Save Changes"}
              </button>
            </div>
          </form>
        </Card>

        <div className="space-y-6">
        <Card testid="documents-card">
          <p className="mb-5 font-serif text-lg text-navy-900">Document Vault</p>
          {docs.length === 0 ? (
            <p className="text-sm leading-relaxed text-slate-500" data-testid="documents-empty">
              No documents yet. Offer letters, internship letters and other files shared by HR will appear here.
            </p>
          ) : (
            <div className="space-y-3" data-testid="documents-list">
              {docs.map((d) => (
                <a
                  key={d.id}
                  href={fileUrl(d.fileUrl)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 rounded-xl border border-navy-900/10 bg-ivory px-4 py-3 transition-colors hover:border-gold"
                  data-testid={`document-${d.id}`}
                >
                  <FileText className="h-5 w-5 shrink-0 text-gold-dark" aria-hidden />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-navy-900">{d.name}</p>
                    <p className="text-[10px] uppercase tracking-wider text-slate-400">{d.category} · {fmtDate(d.uploadedAt)}</p>
                  </div>
                  <Download className="h-4 w-4 shrink-0 text-slate-400" aria-hidden />
                </a>
              ))}
            </div>
          )}
        </Card>

        <Card testid="security-card">
          <p className="mb-5 font-serif text-lg text-navy-900">Change Password</p>
          <form onSubmit={changePw} className="space-y-4" data-testid="change-password-form">
            <Field label="Current Password">
              <input type="password" required autoComplete="current-password" className={inputCls} value={pwForm.current} onChange={(e) => setPwForm({ ...pwForm, current: e.target.value })} data-testid="pw-current" />
            </Field>
            <Field label="New Password">
              <input type="password" required minLength={8} autoComplete="new-password" className={inputCls} value={pwForm.next} onChange={(e) => setPwForm({ ...pwForm, next: e.target.value })} data-testid="pw-new" />
            </Field>
            <Field label="Confirm New Password">
              <input type="password" required minLength={8} autoComplete="new-password" className={inputCls} value={pwForm.confirm} onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })} data-testid="pw-confirm" />
            </Field>
            <button type="submit" disabled={pwSaving} className="btn-gold !px-6 !py-2.5 !text-xs disabled:opacity-60" data-testid="pw-save">
              <KeyRound className="h-4 w-4" aria-hidden /> {pwSaving ? "Updating…" : "Update Password"}
            </button>
          </form>
        </Card>
        </div>
      </div>
    </div>
  );
}
